// src/utils/storageHelper.js
// ─────────────────────────────────────────────────────────────────────────────
// User-prefixed localStorage helper WITH Supabase bidirectional sync.
//
// Your existing components keep writing/reading localStorage directly via
// userKey() — NO COMPONENT CHANGES needed. Background sync (every 15s):
//   PUSH: detects local changes → uploads to Supabase
//   PULL: detects other browsers' changes → updates localStorage
//
// On login:  pulls server data → hydrates localStorage → starts background sync
// During session: bidirectional sync every 15s (push + pull)
// On logout: stops sync → clears localStorage
//
// Usage (unchanged):
//   import { userKey } from '../utils/storageHelper.js';
//   localStorage.setItem(userKey('qais_risk_result'), JSON.stringify(data));
//   localStorage.getItem(userKey('qais_risk_result'));
// ─────────────────────────────────────────────────────────────────────────────

import { supabase } from '../lib/supabaseClient.js';

const SYNC_INTERVAL_MS = 15000;
const SYNCED_KEY_PREFIX = 'qais_';

let periodicSyncTimer = null;
let lastSnapshot = new Map();      // baseKey → raw localStorage value (last synced)
let lastPullTime = null;            // ISO timestamp — latest server updated_at we've pulled

// ═════════════════════════════════════════════════════════════════════════════
// EXISTING FUNCTIONS (unchanged)
// ═════════════════════════════════════════════════════════════════════════════

export const userKey = (key) => {
  const email = localStorage.getItem('qais_user_email') || 'guest';
  return `${email}:${key}`;
};

export const initUserEmail = async () => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (user?.email) {
      localStorage.setItem('qais_user_email', user.email);
      return user.email;
    }
  } catch {}
  return null;
};

export const clearUserKeys = () => {
  const email = localStorage.getItem('qais_user_email');
  if (!email) return;

  const prefix = `${email}:`;
  const keysToRemove = [];
  for (let i = 0; i < localStorage.length; i++) {
    const k = localStorage.key(i);
    if (k && k.startsWith(prefix)) keysToRemove.push(k);
  }
  keysToRemove.forEach(k => localStorage.removeItem(k));
  localStorage.removeItem('qais_user_email');
};

export const migrateLegacyKeys = (email) => {
  if (!email) return;
  const legacyKeys = [
    'qais_onboarding', 'qais_risk_result', 'qais_eu_classifier_result',
    'qais_checklist_setup', 'qais_checklist_progress', 'qais_doc_history',
    'qais_docs_generated', 'qais_chat_history', 'qais_blockchain_saved',
    'qais_blockchain_payload', 'qais_ledger_record', 'qais_journey_done',
    'qais_tooltips_dismissed',
    'qais_action_states_minimal', 'qais_action_states_limited',
    'qais_action_states_high', 'qais_action_states_unacceptable',
  ];
  legacyKeys.forEach(key => {
    const val = localStorage.getItem(key);
    if (val !== null) {
      const prefixedKey = `${email}:${key}`;
      if (!localStorage.getItem(prefixedKey)) {
        localStorage.setItem(prefixedKey, val);
      }
      localStorage.removeItem(key);
    }
  });
};

// ═════════════════════════════════════════════════════════════════════════════
// SERVER SYNC FUNCTIONS
// ═════════════════════════════════════════════════════════════════════════════

/**
 * Called once on SIGNED_IN.
 * 1. Pulls all server data → hydrates localStorage
 * 2. Pushes any local data not yet on server (one-time migration)
 * 3. Captures snapshot + lastPullTime for the bidirectional periodic sync
 */
export const syncFromServer = async () => {
  const email = localStorage.getItem('qais_user_email');
  if (!email) {
    console.warn('[storageHelper] syncFromServer: no email set, skipping');
    return;
  }

  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    // ── 1. PULL: fetch all server rows for this user ─────────────────────
    const { data: rows, error: fetchErr } = await supabase
      .from('user_storage')
      .select('key, value, updated_at');

    if (fetchErr) {
      console.warn('[storageHelper] Server pull failed:', fetchErr.message);
      return;
    }

    const serverKeys = new Set();
    let hydratedCount = 0;
    let maxUpdatedAt = null;

    for (const row of rows || []) {
      serverKeys.add(row.key);
      const prefixedKey = `${email}:${row.key}`;
      const stringValue = typeof row.value === 'string'
        ? row.value
        : JSON.stringify(row.value);

      if (localStorage.getItem(prefixedKey) === null) {
        localStorage.setItem(prefixedKey, stringValue);
        hydratedCount++;
      }

      if (!maxUpdatedAt || row.updated_at > maxUpdatedAt) {
        maxUpdatedAt = row.updated_at;
      }
    }

    // Set lastPullTime so periodic sync knows where to resume
    lastPullTime = maxUpdatedAt || new Date(0).toISOString();

    if (hydratedCount > 0) {
      console.log(`[storageHelper] Restored ${hydratedCount} keys from server`);
    }

    // ── 2. PUSH: upload localStorage data not yet on server ──────────────
    const uploads = [];
    const prefix = `${email}:`;
    for (let i = 0; i < localStorage.length; i++) {
      const fullKey = localStorage.key(i);
      if (!fullKey || !fullKey.startsWith(prefix)) continue;

      const baseKey = fullKey.substring(prefix.length);
      if (!baseKey.startsWith(SYNCED_KEY_PREFIX)) continue;
      if (serverKeys.has(baseKey)) continue;

      const rawValue = localStorage.getItem(fullKey);
      if (rawValue === null) continue;

      let parsedValue;
      try { parsedValue = JSON.parse(rawValue); }
      catch { parsedValue = rawValue; }

      uploads.push({
        user_id: user.id,
        key: baseKey,
        value: parsedValue,
      });
    }

    if (uploads.length > 0) {
      const { error: uploadErr } = await supabase
        .from('user_storage')
        .upsert(uploads, { onConflict: 'user_id,key' });

      if (uploadErr) {
        console.warn('[storageHelper] Migration upload failed:', uploadErr.message);
      } else {
        console.log(`[storageHelper] Migrated ${uploads.length} local keys to server`);
      }
    }

    // ── 3. Initialize snapshot for delta-sync ─────────────────────────────
    captureSnapshot();
  } catch (err) {
    console.warn('[storageHelper] syncFromServer unexpected error:', err);
  }
};

/**
 * Start background bidirectional sync. Call once on SIGNED_IN.
 */
export const startSync = () => {
  if (periodicSyncTimer) return;

  captureSnapshot();
  periodicSyncTimer = setInterval(syncBidirectional, SYNC_INTERVAL_MS);

  document.addEventListener('visibilitychange', flushOnVisibilityChange);
  window.addEventListener('beforeunload', syncBidirectional);

  console.log('[storageHelper] Background sync started (bidirectional, 15s interval)');
};

/**
 * Stop background sync. Call on SIGNED_OUT.
 */
export const stopSync = () => {
  if (periodicSyncTimer) {
    clearInterval(periodicSyncTimer);
    periodicSyncTimer = null;
  }
  document.removeEventListener('visibilitychange', flushOnVisibilityChange);
  window.removeEventListener('beforeunload', syncBidirectional);
  lastSnapshot.clear();
  lastPullTime = null;
  console.log('[storageHelper] Background sync stopped');
};

// ── INTERNAL ─────────────────────────────────────────────────────────────────

function captureSnapshot() {
  const email = localStorage.getItem('qais_user_email');
  if (!email) return;

  lastSnapshot.clear();
  const prefix = `${email}:`;
  for (let i = 0; i < localStorage.length; i++) {
    const fullKey = localStorage.key(i);
    if (!fullKey || !fullKey.startsWith(prefix)) continue;
    const baseKey = fullKey.substring(prefix.length);
    if (!baseKey.startsWith(SYNCED_KEY_PREFIX)) continue;
    lastSnapshot.set(baseKey, localStorage.getItem(fullKey));
  }
}

/**
 * The bidirectional sync. Runs on a 15-second interval.
 * Phase 1: PUSH — detect local changes, upload to server
 * Phase 2: PULL — fetch server changes newer than our last pull, apply locally
 */
async function syncBidirectional() {
  const email = localStorage.getItem('qais_user_email');
  if (!email) return;

  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    // ── PHASE 1: PUSH — upload any local changes since last sync ─────────
    const uploads = [];
    const prefix = `${email}:`;

    for (let i = 0; i < localStorage.length; i++) {
      const fullKey = localStorage.key(i);
      if (!fullKey || !fullKey.startsWith(prefix)) continue;
      const baseKey = fullKey.substring(prefix.length);
      if (!baseKey.startsWith(SYNCED_KEY_PREFIX)) continue;

      const currentValue = localStorage.getItem(fullKey);
      if (lastSnapshot.get(baseKey) === currentValue) continue;

      let parsedValue;
      try { parsedValue = JSON.parse(currentValue); }
      catch { parsedValue = currentValue; }

      uploads.push({
        user_id: user.id,
        key: baseKey,
        value: parsedValue,
      });
    }

    if (uploads.length > 0) {
      const { error: pushErr } = await supabase
        .from('user_storage')
        .upsert(uploads, { onConflict: 'user_id,key' });

      if (pushErr) {
        console.warn('[storageHelper] Push failed:', pushErr.message);
      } else {
        // Update snapshot for keys we just pushed
        uploads.forEach(u => {
          const fullKey = `${email}:${u.key}`;
          lastSnapshot.set(u.key, localStorage.getItem(fullKey));
        });
        console.log(`[storageHelper] Pushed ${uploads.length} keys to server`);
      }
    }

    // ── PHASE 2: PULL — fetch server changes from other browsers ────────
    const cutoff = lastPullTime || new Date(0).toISOString();

    const { data: rows, error: pullErr } = await supabase
      .from('user_storage')
      .select('key, value, updated_at')
      .gt('updated_at', cutoff);

    if (pullErr) {
      console.warn('[storageHelper] Pull failed:', pullErr.message);
      return;
    }

    let pulledCount = 0;
    for (const row of rows || []) {
      const prefixedKey = `${email}:${row.key}`;
      const stringValue = typeof row.value === 'string'
        ? row.value
        : JSON.stringify(row.value);

      // Only apply if it's different from what we already have synced
      // (this skips rows we just pushed up ourselves)
      if (lastSnapshot.get(row.key) !== stringValue) {
        localStorage.setItem(prefixedKey, stringValue);
        lastSnapshot.set(row.key, stringValue);
        pulledCount++;
      }

      // Track the latest server timestamp for next iteration
      if (!lastPullTime || row.updated_at > lastPullTime) {
        lastPullTime = row.updated_at;
      }
    }

    if (pulledCount > 0) {
      console.log(`[storageHelper] Pulled ${pulledCount} changes from other browsers`);
    }
  } catch (err) {
    console.warn('[storageHelper] syncBidirectional error:', err);
  }
}

function flushOnVisibilityChange() {
  if (document.visibilityState === 'hidden') {
    syncBidirectional();
  }
}
